/**
 * RemittanceSchedulingPage — `/franchise/remittance`.
 *
 * Per-franchise remittance schedule (cadence config) backed by the
 * operations slice (`/v1/franchise/ops/remittance-schedule/franchise/:id`).
 * The page lets the operator set / replace the schedule and surfaces the
 * recent settlement-run history alongside.
 */
import { type JSX, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  getRemittanceSchedule,
  listFranchises,
  listSettlementsForFranchise,
  setRemittanceSchedule,
  type RemittanceCadence,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

const CADENCES: readonly RemittanceCadence[] = ['DAILY', 'WEEKLY', 'BIWEEKLY', 'MONTHLY'];

function formatNgn(decimal: string): string {
  const n = Number.parseFloat(decimal);
  if (!Number.isFinite(n)) return `₦${decimal}`;
  return `₦${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

export function RemittanceSchedulingPage(): JSX.Element {
  const qc = useQueryClient();
  const [franchiseId, setFranchiseId] = useState('');
  const [cadence, setCadence] = useState<RemittanceCadence>('WEEKLY');
  const [dayOfPeriod, setDayOfPeriod] = useState(1);
  const [timeOfDay, setTimeOfDay] = useState('02:00');
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [slaHours, setSlaHours] = useState(24);
  const [active, setActive] = useState(true);
  const [actionErr, setActionErr] = useState<string | null>(null);

  const franchises = useQuery({
    queryKey: ['franchise', 'remittance', 'franchises'],
    queryFn: () => listFranchises({ pageSize: 100 }),
    retry: false,
  });

  const schedule = useQuery({
    queryKey: ['franchise', 'remittance', 'schedule', franchiseId],
    queryFn: () => getRemittanceSchedule(franchiseId),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  // Hydrate the form when the existing schedule loads.
  useEffect(() => {
    const s = schedule.data;
    if (s) {
      setCadence(s.cadence);
      setDayOfPeriod(s.dayOfPeriod);
      setTimeOfDay(s.timeOfDay);
      setSkipWeekends(s.skipWeekends);
      setSlaHours(s.slaHours);
      setActive(s.active);
    }
  }, [schedule.data]);

  const runs = useQuery({
    queryKey: ['franchise', 'remittance', 'runs', franchiseId],
    queryFn: () => listSettlementsForFranchise(franchiseId, { pageSize: 50 }),
    retry: false,
    enabled: franchiseId.length > 0,
  });

  const save = useMutation({
    mutationFn: () =>
      setRemittanceSchedule(franchiseId, {
        cadence,
        dayOfPeriod,
        timeOfDay,
        skipWeekends,
        slaHours,
        active,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'remittance', 'schedule', franchiseId] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const denied =
    runs.isError && runs.error instanceof ApiError &&
    (runs.error.status === 401 || runs.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Remittance scheduling"
        description="Per-franchise remittance cadence + recent payouts."
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium text-muted">Franchise</span>
        {franchises.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : (
          <select
            value={franchiseId}
            onChange={(e) => setFranchiseId(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm flex-1 min-w-[260px]"
          >
            <option value="">Select…</option>
            {franchises.data?.items.map((f) => (
              <option key={f.id} value={f.id}>
                {f.legalName} ({f.franchiseNumber})
              </option>
            ))}
          </select>
        )}
      </div>

      {franchiseId.length === 0 ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
          Pick a franchise to configure remittance.
        </p>
      ) : (
        <>
          <div className="bg-white border border-border rounded-xl p-4 space-y-4" data-testid="remittance-schedule-form">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Remittance cadence</h3>
              {schedule.data ? (
                <span className="text-xs text-muted">
                  Next run:{' '}
                  {schedule.data.nextRunAt
                    ? new Date(schedule.data.nextRunAt).toLocaleString()
                    : '—'}
                </span>
              ) : (
                <span className="text-xs text-muted">No schedule set yet</span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div>
                <label className="block text-xs font-medium text-muted mb-1">Cadence</label>
                <select
                  value={cadence}
                  onChange={(e) => setCadence(e.target.value as RemittanceCadence)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                >
                  {CADENCES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-muted mb-1">Day</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={dayOfPeriod}
                  onChange={(e) => setDayOfPeriod(Number.parseInt(e.target.value, 10) || 1)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted mb-1">Time (HH:MM)</label>
                <input
                  type="text"
                  value={timeOfDay}
                  onChange={(e) => setTimeOfDay(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted mb-1">SLA (hours)</label>
                <input
                  type="number"
                  min="1"
                  max="720"
                  value={slaHours}
                  onChange={(e) => setSlaHours(Number.parseInt(e.target.value, 10) || 24)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-1 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={skipWeekends}
                    onChange={() => setSkipWeekends((v) => !v)}
                  />
                  Skip weekends
                </label>
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-1 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() => setActive((v) => !v)}
                  />
                  Active
                </label>
              </div>
            </div>
            {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}
            <button
              type="button"
              onClick={() => {
                setActionErr(null);
                save.mutate();
              }}
              disabled={save.isPending}
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : schedule.data ? 'Update schedule' : 'Save schedule'}
            </button>
          </div>

          {runs.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : denied ? (
            <DeferredNotice title="Permission required">
              <code className="font-mono">FranchiseSettlement/read</code> required.
            </DeferredNotice>
          ) : runs.isError ? (
            <DeferredNotice title="Service unreachable">{(runs.error as Error).message}</DeferredNotice>
          ) : (runs.data?.items.length ?? 0) === 0 ? (
            <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
              No settlement runs recorded yet.
            </p>
          ) : (
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Period</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Net remitted</th>
                    <th className="text-left px-4 py-2">Ledger ref</th>
                    <th className="text-left px-4 py-2">Completed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {runs.data?.items.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-xs text-muted">
                        {r.fromDate} → {r.toDate}
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs">
                        {formatNgn(r.netRemitted)}
                      </td>
                      <td className="px-4 py-2 font-mono text-[11px] text-muted">
                        {r.ledgerJournalId ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted">
                        {r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
